import React, { useMemo } from 'react';
import Product from '../components/Product';
import OrderBumps, { useHasOrderBumps } from '../components/CheckoutForm/components/OrderBumps';
import Variants from '../components/CheckoutForm/components/Variants';
import CheckoutForm from '../components/CheckoutForm';
import useCheckoutState from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import {
  checkHasEventModules,
  checkHasEventWrapperContent,
  checkHasProductModules,
  checkHasProductWrapperContent,
} from '../enums/modules';
import useCartCost from '../hooks/useCartCost';
import useEffectivePricing from '../hooks/useEffectivePricing';

import clsx from 'clsx';
import CheckoutBrandingBadge from '../components/CheckoutBrandingBadge';
import CheckoutConfirmation from '../CheckoutConfirmation';
import CheckoutFormContainer from '../components/CheckoutFormContainer';
import checkHasVisibleVariants from '../lib/checkHasVisibleVariants';
import { PAGE_TYPES } from '../enums/page-types';
import EventDetails from '../components/EventDetails';
import TicketGroups from '../components/TicketGroups';
import getUrlFromCdn, { CDNFitOption } from '../services/getUrlFromCdn';
import { LogoBanner } from '@repo/checkout/components/Product/Product';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';
import StyledElement from '@repo/checkout/components/StyledElement';
import checkHasMedia from '../lib/checkHasMedia';
import checkHasMediaGallery from '../lib/checkHasMediaGallery';
import ProductPricePicker from '../components/Product/ProductPricePicker';
import { isMultiPrice } from '../lib/multiPrice';
import DocumentBackground from '../components/DocumentBackground';

export default function CheckoutCenter() {
  const {
    checkoutLayout,
    theme,
    checkoutType,
    product,
    checkout,
    seller,
    query,
    isTesting,
    isPreview,
    form,
    isScreenshot,
    isConfirmation,
    isTestingPayments,
    isUpsell,
  } = useCheckoutState();
  const aiTarget = useAiTarget();
  const { productPriceAmount, setupFeeAmount } = useCartCost();
  const { effectiveDiscountedFromPrice, effectiveTrialPeriodDays, effectiveStartDate } =
    useEffectivePricing();
  const hasTicketTypes = checkout.type === PAGE_TYPES.EVENT;
  const hasVisibleVariants = checkHasVisibleVariants(
    product.variants,
    form.variants,
    query,
    isTesting,
  );

  // Unset position stays 'content' here: Center historically rendered these in
  // the content section (Double's unset default is 'form').
  const variantsPosition = theme?.elements?.checkout?.variants?.position || 'content';
  const ticketGroupsPosition = theme?.elements?.checkout?.ticketGroups?.position || 'content';
  const pricesPosition = theme?.elements?.product?.pricePicker?.position || 'content';

  const shouldRenderVariantsInContent = hasVisibleVariants && variantsPosition === 'content';
  const shouldRenderVariantsInForm = hasVisibleVariants && variantsPosition === 'form';

  // Bumps sit under the variants and follow them between columns by default,
  // but unlike variants they are not gated on the product having any: a page
  // with bumps and no variants still has to show them.
  const orderBumpsPosition =
    theme?.elements?.checkout?.form?.orderBumps?.position || variantsPosition;
  const hasOrderBumps = useHasOrderBumps();
  const shouldRenderBumpsInContent = hasOrderBumps && orderBumpsPosition === 'content';
  const shouldRenderBumpsInForm = hasOrderBumps && orderBumpsPosition === 'form';
  const shouldRenderTicketsInContent = hasTicketTypes && ticketGroupsPosition === 'content';
  const shouldRenderTicketsInForm = hasTicketTypes && ticketGroupsPosition === 'form';
  const shouldRenderPricesInForm =
    pricesPosition === 'form' &&
    theme?.elements?.product?.pricePicker?.enabled !== false &&
    isMultiPrice(product?.prices, query);

  const hasProductModules = checkHasProductModules({
    theme,
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: effectiveDiscountedFromPrice,
    trialPeriodDays: effectiveTrialPeriodDays,
    startDate: effectiveStartDate,
    checkoutType,
    checkoutLayout,
  });

  const hasEventModules = checkHasEventModules({ theme, checkout, seller });

  const hasProductWrapperContent = checkHasProductWrapperContent({
    theme,
    checkout,
    seller,
    query,
    isScreenshot,
    productPriceAmount,
    setupFeeAmount,
    discountedFromPrice: effectiveDiscountedFromPrice,
    trialPeriodDays: effectiveTrialPeriodDays,
    startDate: effectiveStartDate,
    checkoutType,
    checkoutLayout,
  });

  const hasEventWrapperContent = checkHasEventWrapperContent({
    theme,
    checkout,
    seller,
    checkoutType,
  });

  const defaultWidth = 1920;
  const defaultHeight = 1080;

  const showLogoBanner =
    checkoutType === checkoutTypes.CHECKOUT &&
    theme.elements?.product?.logo?.enabled !== false &&
    theme.elements?.product?.logo?.hostedPageLogoStyle !== 'logoBubble';

  const hasMedia = checkHasMedia(checkout, product);
  const hasMediaGallery = checkHasMediaGallery(checkout, product);

  // Logo bubble extends above the card via absolute positioning — overflow clipping would cut it off.
  // This applies to popup/embed (always logoBubble) and hosted pages with logoBubble style.
  const hasLogoBubbleOverflow =
    checkoutType === checkoutTypes.POPUP ||
    (checkoutType === checkoutTypes.CHECKOUT &&
      theme.elements?.product?.logo?.hostedPageLogoStyle === 'logoBubble');

  const checkoutClass = useMemo(
    () =>
      clsx('cp_checkout cp_checkout-center relative text-[var(--cp_text-primary)]', {
        'mb-8': checkoutType !== checkoutTypes.EMBED && checkoutType !== checkoutTypes.POPUP,
        'pt-19': checkoutType !== checkoutTypes.EMBED && showLogoBanner,
        'pt-8': checkoutType !== checkoutTypes.EMBED && !showLogoBanner && hasMedia,
        'pt-18': checkoutType !== checkoutTypes.EMBED && !showLogoBanner && !hasMedia,
        'px-4': checkoutType !== checkoutTypes.EMBED,
      }),
    [checkoutType, checkoutLayout, showLogoBanner],
  );

  const cardClass = useMemo(
    () =>
      clsx(
        'cp_card mx-auto w-full relative z-10',
        'border-[var(--cp_checkout-border-color)] [border-width:var(--cp_checkout-border-width)]',
        {
          'shadow-[var(--cp_checkout-shadow)] rounded-[var(--cp_checkout-border-radius)] max-w-screen-xs':
            checkoutType !== checkoutTypes.EMBED,
          '[overflow:clip] [overflow-clip-margin:16px]':
            checkoutType !== checkoutTypes.EMBED && !hasLogoBubbleOverflow && hasMediaGallery,
          '[overflow:hidden]':
            checkoutType !== checkoutTypes.EMBED && !hasLogoBubbleOverflow && !hasMediaGallery,
        },
      ),
    [checkoutType, hasMedia, hasMediaGallery, hasLogoBubbleOverflow],
  );

  const hasContentAboveForm =
    hasProductModules ||
    shouldRenderVariantsInContent ||
    shouldRenderTicketsInContent ||
    hasEventModules;

  const hasFormBlocks =
    shouldRenderPricesInForm ||
    shouldRenderTicketsInForm ||
    shouldRenderVariantsInForm ||
    shouldRenderBumpsInForm;

  const dividerEnabled = theme.elements?.checkout?.divider?.enabled !== false;
  const formAlone = !hasContentAboveForm;

  // The last content item is "flushed" when Product or EventDetails is the
  // last thing above the form AND it has no wrapper content besides media —
  // those components cancel their own pb-4 with marginBottom:-1rem so the
  // media sits directly against the divider. In that state the form needs
  // pt-4 even when the divider is hidden, otherwise the form input sits
  // flush against the image.
  const lastContentItemFlushed =
    hasContentAboveForm &&
    !shouldRenderVariantsInContent &&
    !shouldRenderTicketsInContent &&
    (hasEventModules
      ? !hasEventWrapperContent
      : hasProductModules
        ? !hasProductWrapperContent
        : false);

  // When the content section has a different background color than the form
  // section, the form needs pt-4 even with the divider hidden — otherwise the
  // form input sits flush against the colored content panel with no breathing
  // room. Default (both unset) means both resolve to the same token, so no
  // extra spacing is needed.
  const centerLayout = theme.layout?.centerLayout;
  const contentBgOverride = centerLayout?.contentBackgroundColor;
  const formBgOverride = centerLayout?.formBackgroundColor;
  const contentBgDiffersFromForm =
    (!!contentBgOverride || !!formBgOverride) &&
    (contentBgOverride?.light !== formBgOverride?.light ||
      contentBgOverride?.dark !== formBgOverride?.dark);

  // The single branding badge (our pill or the Stripe trust badge — never both).
  // In embed/popup there is no page background, so it renders inside the form box;
  // in hosted checkouts it sits below the card on the themed background.
  const isEmbedded = checkoutType === checkoutTypes.EMBED || checkoutType === checkoutTypes.POPUP;
  const brandingBadges = <CheckoutBrandingBadge />;

  return (
    <div className={checkoutClass} {...(isUpsell && aiTarget('upsell'))}>
      <DocumentBackground color="var(--cp_background-color)" />
      <LogoBanner />

      <div className={cardClass}>
        {isConfirmation && (
          <div
            className={clsx(
              'bg-[var(--cp_form-background-color)]',
              'rounded-[var(--cp_checkout-border-radius)]',
              '[padding:var(--cp_checkout-padding)]',
            )}
          >
            <CheckoutConfirmation />
          </div>
        )}

        {!isConfirmation && (
          <CheckoutFormContainer>
            {hasContentAboveForm && (
              <div
                className={clsx(
                  'bg-[var(--cp_content-background-color)]',
                  '[padding-top:var(--cp_checkout-padding)]',
                  '[padding-left:var(--cp_checkout-padding)]',
                  '[padding-right:var(--cp_checkout-padding)]',
                  {
                    'rounded-t-[var(--cp_checkout-border-radius)]':
                      checkoutType !== checkoutTypes.EMBED && hasMediaGallery,
                  },
                )}
                style={
                  checkoutType !== checkoutTypes.EMBED
                    ? {
                        borderTopLeftRadius:
                          'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))',
                        borderTopRightRadius:
                          'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))',
                      }
                    : undefined
                }
              >
                {hasProductModules && (
                  <div className="px-4 pb-4">
                    <Product flushMediaBottom={lastContentItemFlushed} />
                  </div>
                )}

                {hasEventModules && (
                  <div className="px-4 pb-4">
                    <EventDetails flushMediaBottom={lastContentItemFlushed} />
                  </div>
                )}

                {shouldRenderTicketsInContent && (
                  <div
                    className={clsx('cp_ticket-types-wrapper pb-4', {
                      'pt-4': !hasEventModules,
                    })}
                  >
                    <TicketGroups />
                  </div>
                )}

                {shouldRenderVariantsInContent && (
                  <div
                    className={clsx('cp_variants-wrapper pb-4', {
                      'pt-4': !hasProductModules,
                    })}
                  >
                    <Variants />
                  </div>
                )}

                {shouldRenderBumpsInContent && (
                  <div className="cp_order-bumps-wrapper px-4 pb-4">
                    <OrderBumps />
                  </div>
                )}

                <StyledElement
                  elementPath="checkout.divider"
                  className={clsx(
                    'cp_divider border-b border-[var(--cp_checkout-divider-border-color)]',
                  )}
                />
              </div>
            )}

            <div
              className={clsx(
                'bg-[var(--cp_form-background-color)]',
                '[padding-left:var(--cp_checkout-padding)]',
                '[padding-right:var(--cp_checkout-padding)]',
                '[padding-bottom:var(--cp_checkout-padding)]',
                {
                  'rounded-[var(--cp_checkout-border-radius)]':
                    formAlone && checkoutType !== checkoutTypes.EMBED && !hasMediaGallery,
                },
              )}
              style={{
                '--cp_branding-badge-color': 'var(--cp_branding-badge-color-on-form)',
                ...(formAlone && {
                  paddingTop: 'max(16px, var(--cp_checkout-padding))',
                }),
                ...(hasContentAboveForm && checkoutType !== checkoutTypes.EMBED
                  ? {
                      borderBottomLeftRadius:
                        'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))',
                      borderBottomRightRadius:
                        'max(0px, calc(var(--cp_checkout-border-radius) - var(--cp_checkout-border-width, 0px)))',
                    }
                  : {}),
              }}
            >
              {hasFormBlocks && (
                <div
                  className={clsx({
                    'pt-4':
                      hasContentAboveForm &&
                      (dividerEnabled || lastContentItemFlushed || contentBgDiffersFromForm),
                  })}
                >
                  {shouldRenderPricesInForm && <ProductPricePicker className="px-4 pb-4" />}

                  {shouldRenderTicketsInForm && (
                    <div className="cp_ticket-types-wrapper pb-4">
                      <TicketGroups />
                    </div>
                  )}

                  {shouldRenderVariantsInForm && (
                    <div className="cp_variants-wrapper pb-4">
                      <Variants />
                    </div>
                  )}

                  {shouldRenderBumpsInForm && (
                    <div className="cp_order-bumps-wrapper px-4 pb-4">
                      <OrderBumps />
                    </div>
                  )}
                </div>
              )}

              <div
                className={clsx('px-4', {
                  // Form blocks group with the fields: no divider between them,
                  // and their pb-4 already provides the gap.
                  'pt-4':
                    !hasFormBlocks &&
                    hasContentAboveForm &&
                    (dividerEnabled || lastContentItemFlushed || contentBgDiffersFromForm),
                })}
              >
                <CheckoutForm />
              </div>

              {isEmbedded && <div className="pb-4">{brandingBadges}</div>}
            </div>
          </CheckoutFormContainer>
        )}
      </div>

      {!isEmbedded && <div className="mt-4">{brandingBadges}</div>}

      {checkoutType !== checkoutTypes.EMBED &&
        checkoutType !== checkoutTypes.POPUP &&
        (!isPreview || isTestingPayments) && (
          <div
            className="fixed h-full w-full inset-0 bg-fixed bg-no-repeat bg-cover bg-center -z-10"
            style={{
              backgroundColor: 'var(--cp_background-color)',
              backgroundSize: 'cover',
              backgroundPosition: `var(--cp_background-position)`,
              ...(theme.layout?.centerLayout?.backgroundImage?.url
                ? {
                    backgroundImage: `image-set(url("${getUrlFromCdn(
                      theme.layout.centerLayout.backgroundImage.url,
                      {
                        width: defaultWidth,
                        height: defaultHeight,
                        fit: CDNFitOption.cover,
                      },
                    )}"), url("${getUrlFromCdn(theme.layout.centerLayout.backgroundImage.url, {
                      width: defaultWidth * 2,
                      height: defaultHeight * 2,
                      fit: CDNFitOption.cover,
                    })}") 2x)`,
                    backgroundSize: 'cover',
                    backgroundPosition: `var(--cp_background-position)`,
                  }
                : {}),
            }}
          />
        )}
    </div>
  );
}
